// Spin-the-wheel storefront popup. Vanilla theme JS, no build step, no
// dependencies. Gates itself against localStorage/sessionStorage state and
// merchant config, then builds a 3-frame modal (wheel -> details -> code)
// entirely at runtime — nothing renders into the DOM until the gate passes,
// so a suppressed visitor costs effectively nothing.
//
// The whole boot path is wrapped so any unexpected throw is a silent
// no-op: this widget must never be able to break the storefront page it's
// embedded on.
(function () {
  "use strict";

  if (window.__emberSpinWheelInit) return;
  window.__emberSpinWheelInit = true;

  var scriptEl = document.currentScript || document.querySelector("script[data-proxy-base]");
  var proxyBase = (scriptEl && scriptEl.getAttribute("data-proxy-base")) || "/apps/ember";
  var cssHref = scriptEl && scriptEl.getAttribute("data-css-href");
  var cssRequested = false;
  // Wheel style. "pan" draws the cookware-pan look — wedges in the pan palette
  // (gold / sage / dark-green) plus fixed rim + handles — entirely in code, so
  // it works at any prize count and the handles never rotate with the wedges.
  // Anything else (absent) is the plain cream/sage wheel.
  var panMode = (scriptEl && scriptEl.getAttribute("data-wheel-style")) === "pan";

  // Flat colour chips for the variant picker, mirroring the theme's native
  // product-card option swatches (Shopify swatch.color.rgb, not exposed on the
  // /products/{handle}.js endpoint). A colour not listed here falls back to the
  // variant's product image, then a neutral chip — so an added colour still
  // shows, just without the exact flat chip until it's added below.
  var EMBER_SWATCH = {
    Black: "rgb(0,0,0)",
    "Coorg Green": "rgb(71,82,67)",
    "Nilgiri Blue": "rgb(47,54,66)",
    "Jaipur Terracotta": "rgb(195,133,111)",
    Wine: "rgb(89,5,3)",
    "Himalayan Mist": "rgb(192,186,180)",
    Maroon: "rgb(128,0,0)",
    Sage: "rgb(152,152,131)",
    Teal: "rgb(11,69,71)",
  };

  // The stylesheet is loaded lazily, only once the gate decides the popup
  // will show — suppressed visitors never download it. Injection happens at
  // gate-pass (before the show delay), so the delay absorbs the CSS fetch.
  function ensureStylesheet() {
    if (cssRequested || !cssHref) return;
    cssRequested = true;
    try {
      // A section can ship this <link> in its own Liquid (parser-discovered, so
      // it downloads in parallel with the config fetch rather than after it).
      // Both mark themselves data-sw-css; skip injecting a second identical one.
      if (document.querySelector("link[data-sw-css]")) return;
      var link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssHref;
      link.setAttribute("data-sw-css", "");
      document.head.appendChild(link);
    } catch (e) {
      /* popup renders unstyled rather than not at all */
    }
  }

  var STORAGE_KEY = "ember_spin_wheel";
  var SESSION_ABANDONED_KEY = "ember_spin_wheel_abandoned";
  var DISMISS_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
  // How long an unclaimed spin keeps resuming the claim form on return
  // visits before decaying back to a fresh wheel. The spin row itself never
  // expires server-side; this only bounds how long the popup nags about it.
  var PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  var CONFIG_CACHE_MS = 10 * 60 * 1000;
  var SPIN_FULL_TURNS = 3; // rest offset padding used for the reduced-motion settle
  var REDUCED_MOTION_SPIN_MS = 400;
  // Optimistic spin: the wheel starts turning the instant the button is
  // clicked (a rAF-driven free spin), in parallel with the /spin request, so
  // the network round-trip is hidden inside motion the shopper is already
  // watching. When the server's winning segment arrives, the free spin eases
  // out to land on it. Reduced-motion users skip this and get the old
  // resolve-then-settle path.
  var PRESPIN_RAMP_MS = 600; // accelerate from rest to cruise speed
  var PRESPIN_CRUISE_DPS = 1080; // cruise angular speed, deg/sec (3 turns/sec)
  var LAND_MS = 2600; // ease-out landing duration once the winner is known
  var LAND_MIN_TURNS = 2; // guarantee at least this many more turns while landing
  var WHEEL_FACE_RADIUS = 148; // 296px face diameter / 2 — used only for the divider-line math
  var WHEEL_BOX_SIZE = 344; // the fixed design canvas the whole wheel scales from

  // Label starting size + inward offset (px in from the 148px face radius) by
  // segment count. `size` is the MAX size; fitLabels shrinks it uniformly per
  // wheel so every label fits two lines. `offset` seats the label anchor at
  // ~radius 100 (a touch further in at high counts) so a two-line block clears
  // both the 12-o'clock pointer (outer) and the hub (inner), and — with the
  // 90px max-width — never crosses the rim, at any prize count.
  var LABEL_METRICS = {
    2: { size: 19, offset: 48 },
    3: { size: 18, offset: 48 },
    4: { size: 17, offset: 48 },
    5: { size: 16, offset: 48 },
    6: { size: 15, offset: 48 },
    7: { size: 14, offset: 50 },
    8: { size: 13.5, offset: 52 },
  };

  var FETCH_TIMEOUT_MS = 15000;

  var STRINGS = {
    closeAria: "Close",
    frame1: {
      headingPrefix: "One Spin, One ",
      headingEmphasis: "Discount",
      // "{count}" is replaced with the spelled-out segment count (mock copy:
      // "Four prizes on the wheel, ..." / "Six prizes ..." / "Eight ...").
      subcopyTemplate: "{count} prizes on the wheel, and every kitchen gets one turn. Your code applies at checkout.",
      spinButton: "Spin the wheel",
      noThanks: "No thanks",
      spinError: "Something went wrong — try again.",
      segmentGoneError: "That prize is no longer available — please spin again.",
      spinAlreadyClaimedError: "This spin was already used — spin again for a fresh one.",
    },
    frame2: {
      headingPrefix: "The Wheel ",
      headingEmphasis: "Stopped",
      subcopy: "Add your details and we’ll unlock the code on the next screen.",
      wonPrefix: "You won ",
      emailLabel: "Email address",
      emailPlaceholder: "you@example.com",
      phoneLabel: "Phone number",
      phonePrefix: "+91",
      phonePlaceholder: "98765 43210",
      submitButton: "Unlock my code",
      microcopy: "We’ll send the code to both. Order updates only — no daily mail.",
      emailError: "Enter a full email address, like nandini@gmail.com",
      phoneError: "Mobile numbers are 10 digits",
      abandonWarning: "Leaving now abandons your prize — close again to confirm.",
      genericError: "Something went wrong — try again.",
    },
    frame3: {
      headingPrefix: "Your Code Is ",
      headingEmphasis: "Ready",
      // "{prize}" is the won segment's label (mock copy: "10% off on your
      // first order. Apply it at checkout, or paste it into the cart.").
      subcopyTemplate: "{prize} on your first order. Apply it at checkout, or paste it into the cart.",
      returningHeading: "You’ve already claimed earlier — here’s your code",
      copyButton: "Copy",
      copiedButton: "Copied",
      continueButton: "Continue shopping",
      // Inline section confirmation (the popup closes instead of confirming).
      appliedButton: "Code applied ✓",
      // Inline add-to-cart confirmation: the popup tears down on add, but the
      // in-page section stays, so its button must resolve to a settled state.
      addedButton: "Added to cart ✓",
      addError: "Couldn’t add — please try again. Your code is saved.",
      // Product-offer prizes: the code screen also shows a variant picker and
      // an add-to-cart CTA that applies the code.
      addToCart: "Add to cart",
      chooseVariant: "Choose an option",
      loadingVariants: "Loading options…",
      soldOut: "Sold out",
      variantsError: "Couldn’t load options — copy the code and use it at checkout.",
    },
  };

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // ---- storage helpers ------------------------------------------------------
  // Every access is wrapped: Safari ITP / private-mode / disabled storage
  // must degrade to "popup still works this pageview" rather than crash.

  function safeGetBlob() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function safeSetBlob(patch) {
    try {
      var blob = safeGetBlob();
      for (var k in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, k)) blob[k] = patch[k];
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
    } catch (e) {
      /* storage unavailable — the popup still functions for this pageview */
    }
  }

  function safeSessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* non-fatal */
    }
  }

  // ---- analytics --------------------------------------------------------

  function pushEvent(name, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var event = { event: name };
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) event[k] = payload[k];
      }
      window.dataLayer.push(event);
    } catch (e) {
      /* analytics must never affect the popup */
    }
  }

  // ---- validation / normalization ---------------------------------------

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
  }

  function isValidPhone(value) {
    return /^\d{10}$/.test((value || "").replace(/\D/g, ""));
  }

  function normalizeEmailClient(value) {
    return (value || "").trim().toLowerCase();
  }

  function normalizePhoneClient(value) {
    return (value || "").replace(/\D/g, "");
  }

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (e) {
      return false;
    }
  }

  // ---- tiny DOM helper (mirrors the pattern used by the other embeds) ---

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === "text") node.textContent = attrs[k];
        else if (attrs[k] != null) node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      if (c) node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return node;
  }

  function appendAll(parent, children) {
    children.forEach(function (c) {
      if (c) parent.appendChild(c);
    });
  }

  function clearContent(root) {
    while (root.content.firstChild) root.content.removeChild(root.content.firstChild);
  }

  function buildHeading(prefix, emphasis) {
    return el("h2", { class: "sw-heading", tabindex: "-1" }, [prefix, el("em", { class: "sw-heading-emphasis", text: emphasis })]);
  }

  function buildSubcopy(text) {
    return el("p", { class: "sw-subcopy", text: text });
  }

  // Merchant copy override for `key` (from the admin "Popup text" card,
  // carried on the public config), falling back to the built-in default.
  function copyText(state, key, fallback) {
    var v = state.copy && state.copy[key];
    return typeof v === "string" && v ? v : fallback;
  }

  // Headings keep the italic-serif treatment on the LAST word for default and
  // overridden copy alike — the merchant types plain text, the styling stays.
  function buildHeadingCopy(state, key, defPrefix, defEmphasis) {
    var override = state.copy && state.copy[key];
    if (typeof override === "string" && override.trim()) {
      var words = override.trim().split(/\s+/);
      var emphasis = words.pop();
      var prefix = words.length ? words.join(" ") + " " : "";
      return buildHeading(prefix, emphasis);
    }
    return buildHeading(defPrefix, defEmphasis);
  }

  // The intro line spells the segment count out ("Four prizes on the wheel").
  var COUNT_WORDS = { 2: "Two", 3: "Three", 4: "Four", 5: "Five", 6: "Six", 7: "Seven", 8: "Eight" };

  function frame1Subcopy(state, n) {
    var template = copyText(state, "subcopy1", STRINGS.frame1.subcopyTemplate);
    return template.replace("{count}", COUNT_WORDS[n] || String(n));
  }

  // Per-frame spacing hooks (e.g. submit-button top margins) key off a frame
  // class on the content stack; each render swaps it.
  function setFrameClass(root, name) {
    root.content.className = "sw-content" + (name ? " " + name : "");
  }

  function setFormError(errorEl, message) {
    errorEl.textContent = message || "";
    errorEl.classList.toggle("sw-hidden", !message);
  }

  function setFieldError(field, message) {
    setFormError(field.errorEl, message);
    field.wrap.classList.toggle("sw-field-invalid", !!message);
  }

  // ---- networking helpers -------------------------------------------------

  // Aborts a fetch that hangs past FETCH_TIMEOUT_MS so a stalled connection
  // can't leave the spin/claim button disabled indefinitely. Falls through to
  // a plain fetch on browsers without AbortController — an unbounded request
  // there is strictly no worse than today's behaviour.
  function fetchWithTimeout(url, opts) {
    if (typeof AbortController === "undefined") return fetch(url, opts);
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, FETCH_TIMEOUT_MS);
    var merged = {};
    for (var k in opts) {
      if (Object.prototype.hasOwnProperty.call(opts, k)) merged[k] = opts[k];
    }
    merged.signal = controller.signal;
    return fetch(url, merged).then(
      function (res) {
        clearTimeout(timer);
        return res;
      },
      function (err) {
        clearTimeout(timer);
        throw err;
      },
    );
  }

  // Reads the envelope's first error code ({ data, status, errors }) so a
  // failure response can be routed to a specific message/behaviour instead of
  // the generic retry path. Returns null for a network failure or a shape
  // that doesn't carry one — both fall through to the generic handling.
  function extractErrorCode(body) {
    var err = body && body.errors && body.errors.error && body.errors.error[0];
    return (err && (err.internalCode || err.code)) || null;
  }

  function extractErrorMessage(body) {
    var err = body && body.errors && body.errors.error && body.errors.error[0];
    return (err && err.message) || null;
  }

  // ---- config / gate ------------------------------------------------------

  function isPathExcluded(excludedPaths, pathname) {
    if (!excludedPaths || !excludedPaths.length) return false;
    for (var i = 0; i < excludedPaths.length; i++) {
      if (pathname.indexOf(excludedPaths[i]) === 0) return true;
    }
    return false;
  }

  function fetchConfigDirect() {
    // Time-bounded like spin/claim: a hung App-Proxy config request must fail
    // closed (reject -> fetchConfig returns null -> the section clears its
    // skeleton) rather than leaving the wheel loading forever.
    return fetchWithTimeout(proxyBase + "/spin-wheel/config", { headers: { accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (body) {
        return body && body.status === "SUCCESS" && body.data ? body.data : null;
      });
  }

  function fetchConfig() {
    // A section may kick this request off inline during page parse (see the
    // section's prefetch <script>) so the round trip overlaps this script's own
    // download instead of starting only after it. Reuse that promise ONCE; a
    // prefetch that failed resolves to null, so on an empty result fall back to
    // a direct fetch — otherwise one prefetch blip would hide both the popup and
    // the section for the whole page view. Nulling it prevents reusing a stale
    // or failed promise on a later call.
    var prefetched = window.__swSpinConfigPromise;
    window.__swSpinConfigPromise = null;
    return (prefetched || Promise.resolve(null))
      .then(function (data) {
        return data || fetchConfigDirect();
      })
      .then(function (data) {
        if (!data) return null;
        safeSetBlob({ configCache: data, configFetchedAt: Date.now() });
        return data;
      })
      .catch(function () {
        return null;
      });
  }

  function loadConfig(blob) {
    var cached = blob.configCache;
    var fetchedAt = blob.configFetchedAt;
    if (cached && fetchedAt && Date.now() - fetchedAt < CONFIG_CACHE_MS) {
      return Promise.resolve(cached);
    }
    return fetchConfig();
  }

  function sortSegments(segments) {
    return (segments || []).slice().sort(function (a, b) {
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
  }

  function findSegmentIndex(segments, id) {
    for (var i = 0; i < segments.length; i++) {
      if (segments[i].id === id) return i;
    }
    return -1;
  }

  function init() {
    var blob = safeGetBlob();
    if (blob.completed) return;
    if (blob.dismissedAt && Date.now() - blob.dismissedAt < DISMISS_SNOOZE_MS) return;
    if (safeSessionGet(SESSION_ABANDONED_KEY)) return;

    loadConfig(blob)
      .then(function (config) {
        // wheelEnabled is the master switch (default true server-side); enabled
        // gates the popup specifically. Both must hold for the popup to show.
        if (!config || config.wheelEnabled === false || !config.enabled) return;
        // A wheel needs at least 2 wedges — a malformed/partial config
        // (server sanitizes hand-edited data down to fewer) renders nothing
        // rather than a broken one-wedge wheel.
        if (!config.segments || config.segments.length < 2) return;
        if (isPathExcluded(config.blacklistedPaths, location.pathname)) return;

        ensureStylesheet();
        // An unclaimed spin resumes its claim form instead of a fresh wheel
        // (see settleSpin). Stale pendings decay to a normal wheel.
        var pending =
          blob.pending && blob.pending.spinId && Date.now() - (blob.pending.spunAt || 0) < PENDING_MAX_AGE_MS
            ? blob.pending
            : null;
        var delayMs = Math.max(0, (config.showDelaySeconds || 0) * 1000);
        setTimeout(function () {
          try {
            showPopup(config, pending);
          } catch (e) {
            /* never affect the page */
          }
        }, delayMs);
      })
      .catch(function () {
        /* silent — a config fetch failure means the popup stays absent */
      });
  }

  // ---- wheel geometry -----------------------------------------------------
  // wedge = 360/n; resting rotation = -180/n so a segment centre always sits
  // under the fixed 12-o'clock pointer at rest. Because the resting rotation
  // is exactly half a wedge, a label's "upright at rest" counter-rotation
  // collapses to a clean function of its own index — see buildWheelFace.

  function conicStartDeg(n) {
    return -180 / n;
  }

  function labelMetricsFor(n) {
    return LABEL_METRICS[n] || LABEL_METRICS[Math.max(2, Math.min(8, n))];
  }

  // The pan palette cycles gold -> sage -> dark-green per wedge (wedge 0, at
  // the pointer, is gold). Returns the fill colour var for wedge i.
  var PAN_PALETTE = ["var(--sw-pan-gold)", "var(--sw-pan-sage)", "var(--sw-green)"];
  function panWedgeColor(i) {
    return PAN_PALETTE[i % 3];
  }
  // Wedge i sits on a dark-green fill when its palette slot is the green one.
  function panWedgeIsDark(i) {
    return i % 3 === 2;
  }

  function buildWheelGradient(n) {
    var wedge = 360 / n;
    // Pan mode: the three-colour cycle above. Plain mode: cream/sage
    // alternating at even counts, single cream at odd counts. Boundary lines
    // are separate constant-width divider bars (see buildWheelFace), so the
    // gradient carries only fills and a divider is 1px at every radius.
    var stops = [];
    for (var i = 0; i < n; i++) {
      var start = i * wedge;
      var end = start + wedge;
      var color = panMode
        ? panWedgeColor(i)
        : n % 2 === 0
          ? i % 2 === 0
            ? "var(--sw-cream)"
            : "var(--sw-sage)"
          : "var(--sw-cream)";
      stops.push(color + " " + start.toFixed(3) + "deg " + end.toFixed(3) + "deg");
    }
    return "conic-gradient(" + stops.join(", ") + ")";
  }

  function buildWheelFace(segments) {
    var n = segments.length;
    var metrics = labelMetricsFor(n);
    var restDeg = conicStartDeg(n);
    var radius = WHEEL_FACE_RADIUS - metrics.offset;
    var wedge = 360 / n;

    var faceEl = el("div", { class: "sw-wheel-face" });
    if (panMode) faceEl.classList.add("sw-wheel-face--pan");
    faceEl.style.transform = "rotate(" + restDeg + "deg)";
    faceEl.style.background = buildWheelGradient(n);
    // Segment boundary lines: one hub-to-rim bar per wedge edge, rotated in
    // the face's own frame so they spin with it. The bar hangs downward from
    // the hub (transform-origin: top center), so boundary angle b needs
    // rotate(b + 180) to point along b. Painted before the labels so labels
    // sit above the lines, as in the mock.
    for (var d = 0; d < n; d++) {
      var bar = el("span", { class: "sw-wheel-divider", "aria-hidden": "true" });
      bar.style.transform = "rotate(" + (d * wedge + 180) + "deg)";
      faceEl.appendChild(bar);
    }

    var labelTextEls = [];
    segments.forEach(function (seg, i) {
      // Wedge i spans [i*wedge, (i+1)*wedge] in face coordinates — the label
      // anchors at its CENTRE (+wedge/2). With the face's -180/n rest
      // rotation this puts label 0 exactly under the pointer, and the
      // upright counter-rotation collapses to -(i*wedge).
      var angle = i * wedge + wedge / 2;
      var restCounter = -restDeg - angle;
      var anchor = el("span", { class: "sw-label-anchor" });
      anchor.style.transform = "rotate(" + angle + "deg)";
      var arm = el("span", { class: "sw-label-arm" });
      arm.style.transform = "translateY(-" + radius + "px)";
      var text = el("span", { class: "sw-label-text", text: seg.label });
      text.style.fontSize = metrics.size + "px";
      // Pan wheel: labels on a dark-green wedge need cream ink to read; the
      // gold/sage wedges keep the default green ink.
      if (panMode && panWedgeIsDark(i)) {
        text.classList.add("sw-label-text--onDark");
      }
      text.style.transform = "translate(-50%, -50%) rotate(" + restCounter + "deg)";
      arm.appendChild(text);
      anchor.appendChild(arm);
      faceEl.appendChild(anchor);
      labelTextEls.push({ el: text, restCounter: restCounter });
    });

    return { faceEl: faceEl, restDeg: restDeg, labelTextEls: labelTextEls };
  }

  function buildWheelVisual(segments) {
    var face = buildWheelFace(segments);
    var faceWrap = el("div", { class: "sw-wheel-face-wrap" }, [face.faceEl]);
    var rim = el("div", { class: "sw-wheel-rim" }, [faceWrap]);
    // Spans, not divs: the theme's base.css hides EMPTY divs (div:empty
    // { display: none }), which would erase these two content-less visuals
    // on the storefront while they render fine in isolation.
    var bloom = el("span", { class: "sw-wheel-bloom" });
    var pointer = el("span", { class: "sw-wheel-pointer" });
    var knob = el("div", { class: "sw-wheel-knob" }, [el("span", { class: "sw-wheel-knob-dot" })]);
    var children = [bloom, rim, pointer, knob];
    // Pan handles: two fixed loops at 12 and 6 o'clock. They are siblings of
    // the rim (NOT of the spinning face), so they stay upright while the wedges
    // turn under them — the whole reason the pan is drawn, not a flat image.
    if (panMode) {
      children.push(el("span", { class: "sw-wheel-handle sw-wheel-handle--top", "aria-hidden": "true" }));
      children.push(el("span", { class: "sw-wheel-handle sw-wheel-handle--bottom", "aria-hidden": "true" }));
    }
    var wheelEl = el("div", { class: "sw-wheel", "aria-hidden": "true" }, children);
    if (panMode) wheelEl.classList.add("sw-wheel--pan");
    // The wheel is drawn on a fixed 344px canvas (all geometry — label radii,
    // rim, knob — is in design pixels) and scaled DOWN as one unit to fit
    // narrow containers. Scaling the box instead of fluid-sizing it is what
    // keeps labels riding their wedges at every width.
    var fitEl = el("div", { class: "sw-wheel-fit" }, [wheelEl]);
    return {
      wheelEl: wheelEl,
      fitEl: fitEl,
      faceWrap: faceWrap,
      faceEl: face.faceEl,
      restDeg: face.restDeg,
      labelTextEls: face.labelTextEls,
    };
  }

  // Shrink the shared label font until every label fits within MAX_LABEL_LINES
  // lines. A long merchant label ("Set of 3 tools – At 599") would otherwise
  // wrap into four or five stacked lines that overflow the face radius and
  // collide with the pointer. Shrinking one shared size (not per-label) keeps
  // the wheel visually even. Must run with the wheel in the DOM — scrollHeight
  // only reflects the wrapped height once laid out. LABEL_METRICS seats the
  // anchor so the resulting two-line block clears the rim, pointer, and hub.
  var LABEL_LINE_HEIGHT = 1.12; // must match .sw-label-text line-height
  var MAX_LABEL_LINES = 2;
  var MIN_LABEL_FONT = 9;
  function fitLabels(labelTextEls) {
    if (!labelTextEls.length || !labelTextEls[0].el.isConnected) return;
    var size = parseFloat(labelTextEls[0].el.style.fontSize) || 14;
    function anyOverflows() {
      var cap = Math.ceil(size * LABEL_LINE_HEIGHT * MAX_LABEL_LINES) + 1;
      for (var i = 0; i < labelTextEls.length; i++) {
        if (labelTextEls[i].el.scrollHeight > cap) return true;
      }
      return false;
    }
    var guard = 0;
    while (size > MIN_LABEL_FONT && anyOverflows() && guard++ < 40) {
      size -= 0.5;
      for (var i = 0; i < labelTextEls.length; i++) {
        labelTextEls[i].el.style.fontSize = size + "px";
      }
    }
  }

  // Applies (or resets) the spin delta on top of the wheel's resting
  // rotation. `animate` toggles the CSS transition class; the label texts
  // get the exact negative of the face's delta on the same transition, so
  // they stay upright at every instant of the tween, not just at the ends.
  function applyRotation(root, spinDeltaDeg, animate) {
    var wheel = root.wheel;
    wheel.faceEl.classList.toggle("sw-spinning", !!animate);
    wheel.faceEl.style.transform = "rotate(" + (wheel.restDeg + spinDeltaDeg) + "deg)";
    wheel.labelTextEls.forEach(function (item) {
      item.el.classList.toggle("sw-spinning", !!animate);
      item.el.style.transform = "translate(-50%, -50%) rotate(" + (item.restCounter - spinDeltaDeg) + "deg)";
    });
  }

  function redrawWheel(root, state) {
    var face = buildWheelFace(state.segments);
    root.wheel.faceWrap.innerHTML = "";
    root.wheel.faceWrap.appendChild(face.faceEl);
    root.wheel.faceEl = face.faceEl;
    root.wheel.restDeg = face.restDeg;
    root.wheel.labelTextEls = face.labelTextEls;
    applyRotation(root, 0, false);
    fitLabels(root.wheel.labelTextEls);
  }

  // ---- modal shell --------------------------------------------------------

  function buildModalShell() {
    var content = el("div", { class: "sw-content" });
    var closeBtn = el("button", { type: "button", class: "sw-close", "aria-label": STRINGS.closeAria, text: "×" });
    var modal = el("div", { class: "sw-modal", role: "dialog", "aria-modal": "true", "aria-label": "Spin the wheel", tabindex: "-1" }, [
      closeBtn,
      content,
    ]);
    var overlay = el("div", { class: "sw-overlay" }, [modal]);

    var root = { overlay: overlay, modal: modal, closeBtn: closeBtn, content: content, wheel: null, state: null };

    closeBtn.addEventListener("click", function () {
      attemptClose(root);
    });
    overlay.addEventListener("mousedown", function (e) {
      if (e.target === overlay) attemptClose(root);
    });

    return root;
  }

  function openModal(root) {
    root.previousActive = document.activeElement;
    // Lock background scroll: the overlay is position:fixed but the page
    // underneath still scrolls on wheel/trackpad. A class on <html> (styled
    // with !important in spin-wheel.css) is used instead of inline overflow
    // because the theme sets `overflow-x: clip` on <body>, which an inline
    // style can't reliably beat.
    document.documentElement.classList.add("sw-scroll-locked");
    root._keyHandler = function (e) {
      handleModalKeydown(root, e);
    };
    document.addEventListener("keydown", root._keyHandler, true);
    requestAnimationFrame(function () {
      root.overlay.classList.add("sw-open");
      // renderFrameWheel already moved focus to the heading; only fall back
      // to the first focusable control if that didn't take (heading focus
      // relies on tabindex="-1", which every supported browser honours).
      if (!root.modal.contains(document.activeElement)) focusFirst(root.modal);
    });
  }

  function closeModal(root) {
    // Inline section has no overlay to tear down and nothing to dismiss — the
    // wheel lives in the page. Any close request (spin DISABLED, "continue",
    // add-to-cart) is a no-op; the surface stays put.
    if (root.inline) return;
    if (root._keyHandler) document.removeEventListener("keydown", root._keyHandler, true);
    if (root._wheelResizeHandler) {
      window.removeEventListener("resize", root._wheelResizeHandler);
      root._wheelResizeHandler = null;
    }
    // A close mid-spin or mid-copy-reset must cancel the pending timer — left
    // running, it fires against a modal that's already gone (renderFrameDetails
    // on a torn-down root, or a stray textContent write on a detached button)
    // and, for the spin timer, fires spin_wheel_spun for a spin the shopper
    // never actually finished watching.
    if (root._spinTimer) {
      clearTimeout(root._spinTimer);
      root._spinTimer = null;
    }
    // Cancel an in-flight free-spin / landing rAF loop (optimistic spin) so it
    // can't keep firing applyRotation against a torn-down modal.
    if (root._spinRAF) {
      cancelAnimationFrame(root._spinRAF);
      root._spinRAF = null;
    }
    if (root._copyResetTimer) {
      clearTimeout(root._copyResetTimer);
      root._copyResetTimer = null;
    }
    // Release the background scroll lock set in openModal.
    document.documentElement.classList.remove("sw-scroll-locked");
    if (root.overlay.parentNode) root.overlay.parentNode.removeChild(root.overlay);
    if (root.previousActive && typeof root.previousActive.focus === "function")
      root.previousActive.focus({ preventScroll: true });
  }

  function handleModalKeydown(root, e) {
    if (e.key === "Escape" || e.key === "Esc") {
      e.preventDefault();
      attemptClose(root);
      return;
    }
    if (e.key === "Tab") trapTab(root.modal, e);
  }

  function trapTab(modal, e) {
    var focusables = modal.querySelectorAll(FOCUSABLE_SELECTOR);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function focusFirst(modal) {
    var focusables = modal.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusables.length) focusables[0].focus({ preventScroll: true });
    else modal.focus({ preventScroll: true });
  }

  // Closing behaviour is frame-dependent: wheel frame snoozes 30 days;
  // details frame warns once then abandons for the session; code frame just
  // closes (the reward is already permanently recorded).
  function attemptClose(root) {
    if (root.inline) return; // nothing to close/dismiss for an in-page section
    var state = root.state;
    if (state.frame === "wheel") {
      safeSetBlob({ dismissedAt: Date.now() });
      pushEvent("spin_wheel_dismissed", { stage: "wheel" });
      closeModal(root);
    } else if (state.frame === "details") {
      if (!state.abandonWarned) {
        state.abandonWarned = true;
        showAbandonWarning(root);
        return;
      }
      safeSessionSet(SESSION_ABANDONED_KEY, "1");
      pushEvent("spin_wheel_abandoned", { prize_label: state.prizeLabel });
      closeModal(root);
    } else {
      closeModal(root);
    }
  }

  function showAbandonWarning(root) {
    if (root.content.querySelector(".sw-abandon-warning")) return;
    var warn = el("p", { class: "sw-abandon-warning", role: "alert", text: STRINGS.frame2.abandonWarning });
    root.content.insertBefore(warn, root.content.firstChild);
  }

  // ---- frame 1: wheel -------------------------------------------------

  // `initialError`, when set, surfaces a message from a claim that bounced the
  // shopper back here (a stale segment or an already-claimed spin) — the wheel
  // itself is already fresh (the caller refetches config before calling this).
  function renderFrameWheel(root, state, initialError) {
    state.frame = "wheel";
    clearContent(root);
    setFrameClass(root, "sw-frame-wheel");

    var heading = buildHeadingCopy(state, "heading1", STRINGS.frame1.headingPrefix, STRINGS.frame1.headingEmphasis);
    var subcopy = buildSubcopy(frame1Subcopy(state, state.segments.length));
    var wheelVisual = buildWheelVisual(state.segments);
    root.wheel = wheelVisual;

    var errorEl = el("p", { class: "sw-form-error sw-hidden", role: "alert" });
    if (initialError) setFormError(errorEl, initialError);
    var spinBtn = el("button", { type: "button", class: "sw-btn sw-btn-primary", text: copyText(state, "spinButton", STRINGS.frame1.spinButton) });
    // "No thanks" is a popup dismissal — meaningless for an in-page section, so
    // it isn't rendered inline (there is nothing to dismiss).
    var noThanks = root.inline
      ? null
      : el("button", { type: "button", class: "sw-link", text: copyText(state, "dismissLink", STRINGS.frame1.noThanks) });

    spinBtn.addEventListener("click", function () {
      handleSpinClick(root, state, spinBtn, errorEl);
    });
    if (noThanks)
      noThanks.addEventListener("click", function () {
        attemptClose(root);
      });

    appendAll(root.content, [heading, subcopy, wheelVisual.fitEl, errorEl, spinBtn, noThanks]);
    fitLabels(wheelVisual.labelTextEls);
    fitWheel(wheelVisual);
    if (root._wheelResizeHandler) window.removeEventListener("resize", root._wheelResizeHandler);
    root._wheelResizeHandler = function () {
      fitWheel(wheelVisual);
    };
    window.addEventListener("resize", root._wheelResizeHandler);
    // Popup moves focus to the heading (modal a11y); inline must not steal
    // focus on page load — it's just one section among many.
    if (!root.inline) heading.focus({ preventScroll: true });
  }

  // Scale factor: full size when the container fits the 344px canvas; below a
  // 420px viewport the wheel takes 84% of the container width (mock rule).
  function fitWheel(wheelVisual) {
    var fit = wheelVisual.fitEl;
    if (!fit.parentNode) return;
    var w = fit.clientWidth;
    if (!w) return;
    var frac = window.innerWidth <= 420 ? 0.84 : 1;
    var s = Math.min(1, (w * frac) / WHEEL_BOX_SIZE);
    // Resize events fire in bursts; skip the style writes when the computed
    // scale hasn't actually changed so a drag-resize doesn't thrash layout.
    if (wheelVisual._appliedScale === s) return;
    wheelVisual._appliedScale = s;
    wheelVisual.wheelEl.style.transform = "translateX(-50%) scale(" + s + ")";
    fit.style.height = Math.round(WHEEL_BOX_SIZE * s) + "px";
  }

  function handleSpinClick(root, state, spinBtn, errorEl) {
    if (state.spinning) return; // double-click guard
    state.spinning = true;
    spinBtn.disabled = true;
    setFormError(errorEl, null);

    // Start the wheel turning NOW, before the request — the /spin round-trip
    // then happens while it's already spinning, so there's no dead gap between
    // the click and motion. Reduced-motion users get no free spin.
    var reduced = prefersReducedMotion();
    if (!reduced) startFreeSpin(root);

    fetchWithTimeout(proxyBase + "/spin-wheel/spin", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (body) {
        if (!body || body.status !== "SUCCESS" || !body.data) {
          throw { spinErrorCode: extractErrorCode(body) };
        }
        var data = body.data;
        var index = findSegmentIndex(state.segments, data.segmentId);
        if (index !== -1) return settleSpin(root, state, index, data, reduced);

        // Stale-wheel resync: the client's cached config no longer matches
        // what the server just picked from (an admin edit landed mid-cache).
        // Cancel the free spin, refetch, redraw (which resets the wheel to
        // rest), and only then settle — the landing target must exist and the
        // free-spin delta is void once the face is rebuilt.
        return fetchConfig().then(function (freshConfig) {
          if (!freshConfig) throw new Error("resync failed");
          stopSpinAnimation(root);
          state.segments = sortSegments(freshConfig.segments);
          state.configVersion = freshConfig.version;
          state.copy = freshConfig.copyOverrides || state.copy;
          redrawWheel(root, state);
          var idx2 = findSegmentIndex(state.segments, data.segmentId);
          if (idx2 === -1) throw new Error("segment missing after resync");
          return settleSpin(root, state, idx2, data, reduced);
        });
      })
      .catch(function (err) {
        // Stop the free spin and return the wheel to rest so a failed spin
        // doesn't leave it frozen at a random angle.
        stopSpinAnimation(root);
        applyRotation(root, 0, false);
        state.spinning = false;
        spinBtn.disabled = false;
        // The merchant just turned the feature off — closing quietly beats
        // showing an error for a popup that shouldn't exist anymore. Also
        // drop the cached config: with it intact the gate would re-show the
        // popup on every pageview (and re-close it on every spin) until the
        // 10-minute client cache expired.
        if (err && err.spinErrorCode === "DISABLED") {
          safeSetBlob({ configCache: null, configFetchedAt: 0 });
          closeModal(root);
          return;
        }
        setFormError(errorEl, STRINGS.frame1.spinError);
      });
  }

  // Free spin: accelerate from rest to a constant cruise speed and hold it,
  // driven frame-by-frame so applyRotation keeps the labels upright. The
  // accumulated delta is stashed on root so the landing can continue from the
  // exact current angle (no jump). Runs until settleSpin or stopSpinAnimation.
  function startFreeSpin(root) {
    var cruiseDpms = PRESPIN_CRUISE_DPS / 1000;
    // Distance covered during the linear ramp (area under the velocity ramp).
    var rampDelta = 0.5 * cruiseDpms * PRESPIN_RAMP_MS;
    root._spinState = { delta: 0 };
    var startTs = null;
    function frame(ts) {
      if (startTs === null) startTs = ts;
      var t = ts - startTs;
      var delta =
        t < PRESPIN_RAMP_MS
          ? 0.5 * cruiseDpms * (t * t) / PRESPIN_RAMP_MS // integral of the linear ramp
          : rampDelta + cruiseDpms * (t - PRESPIN_RAMP_MS);
      root._spinState.delta = delta;
      applyRotation(root, delta, false);
      root._spinRAF = requestAnimationFrame(frame);
    }
    root._spinRAF = requestAnimationFrame(frame);
  }

  function stopSpinAnimation(root) {
    if (root._spinRAF) {
      cancelAnimationFrame(root._spinRAF);
      root._spinRAF = null;
    }
    root._spinState = null;
  }

  // Land on the won segment. Non-reduced: ease out from the free spin's
  // current angle to a target that both lands the segment under the pointer
  // and adds at least LAND_MIN_TURNS more turns (so it always rounds forward,
  // never snaps backward). Reduced-motion: skip straight to the resting
  // position and advance after a short beat.
  function settleSpin(root, state, index, data, reduced) {
    state.spinId = data.spinId;
    state.prizeLabel = data.prizeLabel;
    // The spin is committed server-side at this point — persist it so a
    // refresh (or a return visit) resumes THIS prize's claim form instead of
    // granting a fresh wheel. Closes casual spin-until-jackpot; the claim
    // handlers clear it on success or on a dead spin.
    safeSetBlob({ pending: { spinId: data.spinId, prizeLabel: data.prizeLabel, spunAt: Date.now() } });

    var n = state.segments.length;
    var wedge = 360 / n;
    var landingOffset = (360 - ((index * wedge) % 360)) % 360;

    if (reduced) {
      applyRotation(root, SPIN_FULL_TURNS * 360 + landingOffset, false);
      root._spinTimer = setTimeout(function () {
        root._spinTimer = null;
        pushEvent("spin_wheel_spun", { prize_label: state.prizeLabel });
        renderFrameDetails(root, state);
      }, REDUCED_MOTION_SPIN_MS);
      return;
    }

    var startDelta = (root._spinState && root._spinState.delta) || 0;
    // Smallest k*360 + landingOffset that clears the min-turns floor ahead of
    // where the free spin currently is.
    var minTarget = startDelta + LAND_MIN_TURNS * 360;
    var target = Math.ceil((minTarget - landingOffset) / 360) * 360 + landingOffset;
    var distance = target - startDelta;
    stopSpinAnimation(root);

    var startTs = null;
    function easeOutCubic(x) {
      return 1 - Math.pow(1 - x, 3);
    }
    function frame(ts) {
      if (startTs === null) startTs = ts;
      var frac = (ts - startTs) / LAND_MS;
      if (frac >= 1) {
        applyRotation(root, target, false);
        root._spinRAF = null;
        pushEvent("spin_wheel_spun", { prize_label: state.prizeLabel });
        renderFrameDetails(root, state);
        return;
      }
      applyRotation(root, startDelta + distance * easeOutCubic(frac), false);
      root._spinRAF = requestAnimationFrame(frame);
    }
    root._spinRAF = requestAnimationFrame(frame);
  }

  // ---- frame 2: details -------------------------------------------------

  function buildField(opts) {
    var input = el("input", { id: opts.id, type: opts.type, placeholder: opts.placeholder, class: "sw-input" });
    var label = el("label", { for: opts.id, class: "sw-field-label", text: opts.label });
    var error = el("span", { class: "sw-field-error sw-hidden", role: "alert" });
    var wrap = el("div", { class: "sw-field" }, [label, input, error]);
    return { wrap: wrap, input: input, errorEl: error };
  }

  function buildPhoneField() {
    var input = el("input", {
      id: "sw-phone",
      type: "tel",
      inputmode: "numeric",
      maxlength: "10",
      placeholder: STRINGS.frame2.phonePlaceholder,
      class: "sw-input",
    });
    input.addEventListener("input", function () {
      var digits = input.value.replace(/\D/g, "").slice(0, 10);
      if (digits !== input.value) input.value = digits;
    });
    var label = el("label", { for: "sw-phone", class: "sw-field-label", text: STRINGS.frame2.phoneLabel });
    var prefix = el("span", { class: "sw-phone-prefix", text: STRINGS.frame2.phonePrefix });
    var row = el("div", { class: "sw-phone-row" }, [prefix, input]);
    var error = el("span", { class: "sw-field-error sw-hidden", role: "alert" });
    var wrap = el("div", { class: "sw-field" }, [label, row, error]);
    return { wrap: wrap, input: input, errorEl: error };
  }

  function renderFrameDetails(root, state) {
    state.frame = "details";
    state.abandonWarned = false;
    clearContent(root);
    setFrameClass(root, "sw-frame-details");

    var heading = buildHeadingCopy(state, "heading2", STRINGS.frame2.headingPrefix, STRINGS.frame2.headingEmphasis);
    var subcopy = buildSubcopy(copyText(state, "subcopy2", STRINGS.frame2.subcopy));
    var plate = el("div", { class: "sw-prize-plate", "aria-live": "polite" }, [
      el("span", { class: "sw-prize-plate-eyebrow", text: copyText(state, "wonEyebrow", STRINGS.frame2.wonPrefix.trim()) }),
      el("span", { class: "sw-prize-plate-value", text: state.prizeLabel }),
    ]);

    var emailField = buildField({ id: "sw-email", label: STRINGS.frame2.emailLabel, type: "email", placeholder: STRINGS.frame2.emailPlaceholder });
    var phoneField = buildPhoneField();
    var microcopy = el("p", { class: "sw-microcopy", text: copyText(state, "microcopy", STRINGS.frame2.microcopy) });
    var formError = el("p", { class: "sw-form-error sw-hidden", role: "alert" });
    var submitBtn = el("button", { type: "button", class: "sw-btn sw-btn-primary", disabled: "disabled", text: copyText(state, "submitButton", STRINGS.frame2.submitButton) });

    var fieldState = {
      emailErrored: false,
      phoneErrored: false,
    };

    function updateSubmitEnabled() {
      var emailOk = isValidEmail(emailField.input.value);
      var phoneOk = isValidPhone(phoneField.input.value);
      submitBtn.disabled = !(emailOk && phoneOk);
    }

    // Blur validates only a field the visitor actually left content in —
    // an empty field is never flagged just because focus moved away.
    function validateEmailOnBlur() {
      if (emailField.input.value === "") return;
      fieldState.emailErrored = !isValidEmail(emailField.input.value);
      setFieldError(emailField, fieldState.emailErrored ? STRINGS.frame2.emailError : null);
      updateSubmitEnabled();
    }

    function validateEmailLive() {
      if (fieldState.emailErrored) {
        var ok = isValidEmail(emailField.input.value);
        fieldState.emailErrored = !ok;
        setFieldError(emailField, ok ? null : STRINGS.frame2.emailError);
      }
      updateSubmitEnabled();
    }

    function forceValidateEmail() {
      var ok = isValidEmail(emailField.input.value);
      fieldState.emailErrored = !ok;
      setFieldError(emailField, ok ? null : STRINGS.frame2.emailError);
      return ok;
    }

    function validatePhoneOnBlur() {
      if (phoneField.input.value === "") return;
      fieldState.phoneErrored = !isValidPhone(phoneField.input.value);
      setFieldError(phoneField, fieldState.phoneErrored ? STRINGS.frame2.phoneError : null);
      updateSubmitEnabled();
    }

    function validatePhoneLive() {
      if (fieldState.phoneErrored) {
        var ok = isValidPhone(phoneField.input.value);
        fieldState.phoneErrored = !ok;
        setFieldError(phoneField, ok ? null : STRINGS.frame2.phoneError);
      }
      updateSubmitEnabled();
    }

    function forceValidatePhone() {
      var ok = isValidPhone(phoneField.input.value);
      fieldState.phoneErrored = !ok;
      setFieldError(phoneField, ok ? null : STRINGS.frame2.phoneError);
      return ok;
    }

    emailField.input.addEventListener("blur", validateEmailOnBlur);
    emailField.input.addEventListener("input", validateEmailLive);
    phoneField.input.addEventListener("blur", validatePhoneOnBlur);
    phoneField.input.addEventListener("input", validatePhoneLive);

    submitBtn.addEventListener("click", function () {
      handleClaimSubmit(root, state, {
        emailField: emailField,
        phoneField: phoneField,
        formError: formError,
        submitBtn: submitBtn,
        fieldState: fieldState,
        forceValidateEmail: forceValidateEmail,
        forceValidatePhone: forceValidatePhone,
        updateSubmitEnabled: updateSubmitEnabled,
      });
    });

    // Microcopy sits UNDER the submit button, per the mock.
    appendAll(root.content, [heading, subcopy, plate, emailField.wrap, phoneField.wrap, formError, submitBtn, microcopy]);
    if (!root.inline) heading.focus({ preventScroll: true });
  }

  function handleClaimSubmit(root, state, ctx) {
    var emailOk = ctx.forceValidateEmail();
    // Both fields are always required — the server recognises a returning
    // shopper only when email AND phone belong to the same claim, so there
    // is no waived-phone state to special-case.
    var phoneOk = ctx.forceValidatePhone();

    if (!emailOk || !phoneOk) {
      ctx.emailField.input.focus({ preventScroll: true });
      return;
    }

    var email = normalizeEmailClient(ctx.emailField.input.value);
    var phone = normalizePhoneClient(ctx.phoneField.input.value);

    ctx.submitBtn.disabled = true;
    setFormError(ctx.formError, null);

    fetchWithTimeout(proxyBase + "/spin-wheel/claim", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ spinId: state.spinId, email: email, phone: phone }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (body) {
        if (!body || body.status !== "SUCCESS" || !body.data) {
          throw { claimErrorCode: extractErrorCode(body), claimErrorMessage: extractErrorMessage(body) };
        }
        var data = body.data;
        state.code = data.code;
        state.prizeLabel = data.prizeLabel || state.prizeLabel;
        state.conditionsText = data.conditionsText || "";
        state.returning = !!data.returning;
        // Present only for product-offer prizes (the server sends the won
        // product's handle alongside the code); drives the variant picker.
        state.productHandle = data.productHandle || "";
        safeSetBlob({ completed: true, code: state.code, prize: state.prizeLabel, conditionsText: state.conditionsText, productHandle: state.productHandle || "", pending: null });
        pushEvent("generate_lead", { prize_label: state.prizeLabel, returning: state.returning });
        renderFrameCode(root, state);
      })
      .catch(function (err) {
        // spinId is retained on state — a retry re-submits against the same
        // spin without needing another wheel spin.
        ctx.submitBtn.disabled = false;

        var code = err && err.claimErrorCode;
        if (code === "IDENTITY_MISMATCH") {
          // One of the two identities matched an existing claim but not both
          // — the server names the fix; correcting a field and resubmitting
          // reuses the retained spinId.
          setFormError(ctx.formError, (err && err.claimErrorMessage) || STRINGS.frame2.genericError);
          return;
        }
        if (code === "SEGMENT_GONE" || code === "SPIN_ALREADY_CLAIMED") {
          var message =
            code === "SEGMENT_GONE" ? STRINGS.frame1.segmentGoneError : STRINGS.frame1.spinAlreadyClaimedError;
          // This spin can never be claimed — drop the resume state with it.
          safeSetBlob({ pending: null });
          // The wheel that got the shopper here may itself be stale (that's
          // how they ended up on a gone segment or a spent spin) — refetch
          // before showing it again rather than redrawing the same one.
          fetchConfig().then(function (freshConfig) {
            if (freshConfig) {
              state.segments = sortSegments(freshConfig.segments);
              state.configVersion = freshConfig.version;
              state.copy = freshConfig.copyOverrides || state.copy;
            }
            renderFrameWheel(root, state, message);
          });
          return;
        }
        setFormError(ctx.formError, STRINGS.frame2.genericError);
      });
  }

  // ---- frame 3: code ----------------------------------------------------

  function renderFrameCode(root, state) {
    state.frame = "code";
    clearContent(root);
    setFrameClass(root, "sw-frame-code");

    var heading = state.returning
      ? el("h2", { class: "sw-heading", tabindex: "-1", text: copyText(state, "returningHeading", STRINGS.frame3.returningHeading) })
      : buildHeadingCopy(state, "heading3", STRINGS.frame3.headingPrefix, STRINGS.frame3.headingEmphasis);
    var subcopy = buildSubcopy(
      copyText(state, "subcopy3", STRINGS.frame3.subcopyTemplate).replace("{prize}", state.prizeLabel || "Your code"),
    );

    // Mock frame 3: the dashed block holds the code AND the Copy action side
    // by side (code left, divider, "Copy" right); the whole block is a tap
    // target on mobile via the button spanning its cell.
    var codeText = el("span", { class: "sw-code-text", text: state.code });
    var copyBtn = el("button", { type: "button", class: "sw-code-copy", text: STRINGS.frame3.copyButton });
    var codeBlock = el("div", { class: "sw-code-block" }, [codeText, copyBtn]);
    var doCopy = function () {
      copyCode(state.code, codeText).then(function () {
        copyBtn.textContent = STRINGS.frame3.copiedButton;
        pushEvent("spin_wheel_code_copied", { prize_label: state.prizeLabel });
        // Held on root so closeModal can cancel it — otherwise a close right
        // after copying fires this against a button that's already gone.
        if (root._copyResetTimer) clearTimeout(root._copyResetTimer);
        root._copyResetTimer = setTimeout(function () {
          root._copyResetTimer = null;
          copyBtn.textContent = STRINGS.frame3.copyButton;
        }, 2000);
      });
    };
    // The whole dashed block is a tap target (mock rule), not just the Copy
    // button — a click anywhere in it copies.
    codeBlock.addEventListener("click", doCopy);

    var conditions = state.conditionsText ? el("p", { class: "sw-conditions", text: state.conditionsText }) : null;

    if (state.productHandle) {
      // Product-offer prize: the code stays visible, and below it a variant
      // picker + "Add to cart" CTA add the chosen variant with the discount
      // already applied. The picker is populated async from the storefront
      // product JSON (see mountVariantPicker).
      var productImg = el("img", { class: "sw-product-image", alt: "", decoding: "async" });
      var picker = el("div", { class: "sw-variant-picker" });
      // Product thumbnail sits to the left of the colour picker; both fill in
      // from the same /products/{handle}.js fetch (see mountVariantPicker).
      var variantRow = el("div", { class: "sw-variant-row" }, [productImg, picker]);
      var addBtn = el("button", { type: "button", class: "sw-btn sw-btn-primary", text: STRINGS.frame3.addToCart });
      addBtn.disabled = true; // enabled once a purchasable variant is selected
      appendAll(root.content, [heading, subcopy, codeBlock, conditions, variantRow, addBtn]);
      if (!root.inline) heading.focus({ preventScroll: true });
      mountVariantPicker(root, state, picker, addBtn, productImg);
      return;
    }

    var continueBtn = el("button", { type: "button", class: "sw-btn sw-btn-primary", text: copyText(state, "continueButton", STRINGS.frame3.continueButton) });
    continueBtn.addEventListener("click", function () {
      // Mock rule: Continue shopping applies the code to the session cart.
      // Shopify's /discount/<code> endpoint attaches the discount to the cart
      // session; it's attached by the time the 302 returns, so `redirect:
      // "manual"` resolves there and skips downloading the homepage the redirect
      // points at. Fire-and-forget — a failure must never block closing, and
      // checkout still accepts the code typed manually.
      try {
        fetch("/discount/" + encodeURIComponent(state.code), { credentials: "same-origin", redirect: "manual" }).catch(function () {});
      } catch (e) {
        /* closing is the contract; the copyable code remains the fallback */
      }
      // Popup closes on continue; the in-page section has nothing to close, so
      // it confirms in place — the discount is applied and the code stays copyable.
      if (root.inline) {
        continueBtn.textContent = copyText(state, "appliedButton", STRINGS.frame3.appliedButton);
        continueBtn.disabled = true;
      } else {
        closeModal(root);
      }
    });

    appendAll(root.content, [heading, subcopy, codeBlock, conditions, continueBtn]);
    if (!root.inline) heading.focus({ preventScroll: true });
  }

  function variantColor(variant) {
    return (variant.options && variant.options[0]) || variant.title || "Option";
  }

  // Flat colour chip matching the product card: the brand palette first, then
  // the variant's product image, then a neutral chip.
  function swatchFill(variant) {
    var rgb = EMBER_SWATCH[variantColor(variant)];
    if (rgb) return rgb;
    if (variant.featured_image && variant.featured_image.src) return 'url("' + variant.featured_image.src + '")';
    return "var(--sw-sage)";
  }

  // Add the chosen variant to the cart, apply the discount, and open the
  // theme's cart drawer (Dawn <cart-drawer>: renderContents adds the fetched
  // section HTML and opens the drawer). Any failure falls back to the cart
  // permalink, which still adds the product and applies the code.
  function addToCartViaDrawer(root, state, variantId, addBtn) {
    // Apply the discount BEFORE the add, because /cart/add.js renders the
    // requested `sections` (the cart-drawer) server-side against the cart state
    // at add-time. If the code isn't attached to the cart yet, that rendered
    // drawer shows the undiscounted price even though the code is on the cart
    // and checkout is correct — the exact "discount not applying" symptom. So
    // the discount must land on the cart first. `redirect: "manual"` keeps that
    // first hop cheap: Shopify attaches the code to the cart server-side by the
    // time it returns its 302, so we resolve on the 302 and skip following it —
    // otherwise fetch downloads the whole (~1 MB) homepage the redirect points
    // at, purely to throw it away. A discount failure must never block the add:
    // the code stays copyable and still applies at checkout, so we swallow it
    // and add regardless.
    //
    // We use the freshly-rendered `cart-drawer` section rather than the stock
    // Dawn cartDrawer.renderContents(): on this theme's customised drawer that
    // helper produces a broken hybrid layout when the cart was previously empty
    // (the item row collapses, the footer floats). Replacing the whole
    // <cart-drawer> element re-upgrades the custom element cleanly, so the first
    // add renders the same as any later one. Falls back to the cart permalink
    // on any failure.
    fetch("/discount/" + encodeURIComponent(state.code), { credentials: "same-origin", redirect: "manual" })
      .catch(function () {})
      .then(function () {
        return fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: "cart-drawer,cart-icon-bubble",
            sections_url: window.location.pathname,
          }),
          credentials: "same-origin",
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error("cart add failed");
        return res.json();
      })
      .then(function (data) {
        // Reaching here means the add SUCCEEDED (past res.ok + JSON parse), so
        // any failure while rendering the drawer is recovered by showing the
        // cart — the item is already in it, alongside the shopper's other items
        // — never by re-adding (double) or rebuilding the cart from scratch.
        try {
          var sections = (data && data.sections) || {};
          pushEvent("spin_wheel_add_to_cart", { prize_label: state.prizeLabel, variant_id: variantId });
          var doc = sections["cart-drawer"] ? new DOMParser().parseFromString(sections["cart-drawer"], "text/html") : null;
          var fresh = doc ? doc.querySelector("cart-drawer") : null;
          var live = document.querySelector("cart-drawer");
          // Popup tears down here so its spinning button disappears with the
          // overlay; the inline section stays on the page, so closeModal no-ops
          // and its button must be settled explicitly or it spins forever.
          if (root.inline && addBtn) {
            addBtn.classList.remove("sw-btn--loading");
            addBtn.textContent = copyText(state, "addedButton", STRINGS.frame3.addedButton);
            addBtn.disabled = true;
          }
          closeModal(root);
          if (!fresh || !live || !live.parentNode) {
            window.location.assign("/cart");
            return;
          }
          live.replaceWith(fresh); // re-upgrades the custom element cleanly
          // Refresh the header cart-count bubble too (best effort).
          try {
            var bubble = document.getElementById("cart-icon-bubble");
            var bubbleDoc = sections["cart-icon-bubble"]
              ? new DOMParser().parseFromString(sections["cart-icon-bubble"], "text/html")
              : null;
            var bubbleInner = bubbleDoc && (bubbleDoc.querySelector(".shopify-section") || bubbleDoc.body);
            if (bubble && bubbleInner) bubble.innerHTML = bubbleInner.innerHTML;
          } catch (e) {
            /* count badge is cosmetic */
          }
          if (typeof fresh.open === "function") {
            fresh.open();
          } else {
            fresh.classList.add("animate", "active");
            document.body.style.overflow = "hidden";
          }
        } catch (e) {
          window.location.assign("/cart");
        }
      })
      .catch(function () {
        // The ADD itself failed (never reached the success handler), so nothing
        // was added — never rebuild the cart from a permalink, which would wipe
        // the shopper's other items. Re-arm the CTA to retry and show an inline
        // note; the discount code stays copyable and still applies at checkout.
        if (!addBtn) return;
        addBtn.classList.remove("sw-btn--loading");
        addBtn.textContent = copyText(state, "addToCart", STRINGS.frame3.addToCart);
        addBtn.disabled = false;
        var host = addBtn.parentNode;
        if (host && !host.querySelector(".sw-add-error")) {
          addBtn.insertAdjacentElement(
            "afterend",
            el("p", {
              class: "sw-add-error",
              role: "alert",
              style: "color:#b3261e;font-size:13px;margin:8px 0 0;",
              text: copyText(state, "addError", STRINGS.frame3.addError),
            })
          );
        }
      });
  }

  // Product-offer variant picker: flat colour swatches (like the product card)
  // built from /products/{handle}.js, a "Colour: <name>" label, and an
  // Add-to-cart CTA that adds the chosen colour and opens the cart drawer with
  // the code applied. Degrades to the copyable code if the JSON fails.
  function mountVariantPicker(root, state, picker, addBtn, productImg) {
    var selected = null;
    var labelEl = el("span", { class: "sw-variant-label" });
    var swatchRow = el("div", { class: "sw-variant-swatches" }, [
      el("span", { class: "sw-variant-loading", text: STRINGS.frame3.loadingVariants }),
    ]);
    picker.appendChild(labelEl);
    picker.appendChild(swatchRow);

    // Shopify image URLs take a width param; a thumbnail-sized render keeps the
    // download small. No-op on a URL that already carries query params.
    function thumb(src) {
      if (!src) return src;
      return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=200";
    }

    function setImage(src, altText) {
      if (!productImg) return;
      if (src) {
        productImg.src = thumb(src);
        if (altText) productImg.alt = altText;
        productImg.style.display = "";
      } else {
        productImg.style.display = "none";
      }
    }

    function select(variant, node, nodes, optionName) {
      selected = variant;
      labelEl.textContent = optionName + ": " + variantColor(variant);
      nodes.forEach(function (n) {
        n.classList.toggle("sw-swatch--selected", n === node);
      });
      // Swap the thumbnail to the chosen colour when that variant has its own
      // image; otherwise keep whatever's already shown.
      if (variant.featured_image && variant.featured_image.src) {
        setImage(variant.featured_image.src, variantColor(variant));
      }
      // Re-arm the CTA on every selection change: a lingering "Added to cart ✓"
      // (or loading) state belongs to the previously added colour, so a shopper
      // who switches colour after adding can add the newly chosen one.
      addBtn.classList.remove("sw-btn--loading");
      addBtn.textContent = copyText(state, "addToCart", STRINGS.frame3.addToCart);
      addBtn.disabled = false;
    }

    addBtn.addEventListener("click", function () {
      if (!selected || addBtn.disabled) return;
      // Clear a prior failure note before retrying.
      var prevErr = addBtn.parentNode && addBtn.parentNode.querySelector(".sw-add-error");
      if (prevErr) prevErr.remove();
      // Loading state until the drawer opens, the flow settles the button, or an
      // add failure re-arms it with an inline error.
      addBtn.disabled = true;
      addBtn.classList.add("sw-btn--loading");
      addToCartViaDrawer(root, state, selected.id, addBtn);
    });

    fetchWithTimeout("/products/" + encodeURIComponent(state.productHandle) + ".js", { credentials: "same-origin" })
      .then(function (res) {
        return res.json();
      })
      .then(function (product) {
        var variants = (product && product.variants) || [];
        if (!variants.length) throw new Error("no variants");
        var optionName = (product.options && product.options[0] && (product.options[0].name || product.options[0])) || "Colour";
        // Seed the thumbnail with the product's featured image; select() swaps
        // it per-colour if that variant carries its own image.
        setImage(
          (product.featured_image && (product.featured_image.src || product.featured_image)) ||
            (product.images && product.images[0]) ||
            "",
          product.title
        );
        swatchRow.textContent = "";
        var nodes = [];
        var firstAvailable = -1;
        variants.forEach(function (v, i) {
          var node = el("button", { type: "button", class: "sw-swatch", "aria-label": variantColor(v) });
          node.style.setProperty("--sw-swatch-bg", swatchFill(v));
          if (v.available) {
            node.addEventListener("click", function () {
              select(v, node, nodes, optionName);
            });
            if (firstAvailable === -1) firstAvailable = i;
          } else {
            node.disabled = true;
            node.classList.add("sw-swatch--soldout");
            node.setAttribute("aria-label", variantColor(v) + " — " + STRINGS.frame3.soldOut);
          }
          nodes.push(node);
          swatchRow.appendChild(node);
        });
        // Auto-select the first purchasable colour so a single-colour product
        // is one tap; choosing another is optional.
        if (firstAvailable !== -1) {
          select(variants[firstAvailable], nodes[firstAvailable], nodes, optionName);
        } else {
          labelEl.textContent = optionName;
          addBtn.disabled = true;
        }
      })
      .catch(function () {
        // Product JSON unreachable — fall back to the copyable code (checkout
        // still accepts it). Drop the thumbnail, picker, and CTA; show a note.
        setImage("", "");
        picker.textContent = "";
        picker.appendChild(el("span", { class: "sw-variant-error", text: STRINGS.frame3.variantsError }));
        addBtn.remove();
      });
  }

  function copyCode(code, codeBlock) {
    if (window.navigator && navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(code).catch(function () {
        return selectTextFallback(codeBlock);
      });
    }
    return selectTextFallback(codeBlock);
  }

  function selectTextFallback(codeBlock) {
    try {
      var range = document.createRange();
      range.selectNodeContents(codeBlock);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      /* selection unsupported — the code remains visible and manually selectable */
    }
    return Promise.resolve();
  }

  // ---- boot -----------------------------------------------------------

  // Per-session UI state. `pending` resumes an unclaimed spin at the claim
  // form; `completed` resumes a prior WIN straight to the code screen so a
  // returning visitor sees their prize instead of a fresh wheel (a spin they
  // can never re-take — the backend allows one per visitor).
  function buildState(config, pending, completed) {
    var state = {
      segments: sortSegments(config.segments),
      configVersion: config.version,
      // Merchant text edits from the admin's "Popup text" card, served on the
      // public config; missing/empty keys fall back to STRINGS defaults.
      copy: config.copyOverrides || {},
      frame: "wheel",
      spinning: false,
      abandonWarned: false,
      spinId: null,
      prizeLabel: null,
      conditionsText: "",
      code: null,
      returning: false,
      // Non-empty when the won prize is a product offer — the code screen then
      // shows a variant picker + add-to-cart CTA for this product handle.
      productHandle: "",
    };
    if (completed) {
      state.code = completed.code;
      state.prizeLabel = completed.prize;
      state.conditionsText = completed.conditionsText || "";
      state.productHandle = completed.productHandle || "";
      state.returning = true;
      state.frame = "code";
    } else if (pending) {
      state.spinId = pending.spinId;
      state.prizeLabel = pending.prizeLabel;
      state.frame = "details";
    }
    return state;
  }

  function renderInitialFrame(root, state) {
    if (state.frame === "code") renderFrameCode(root, state);
    else if (state.frame === "details") renderFrameDetails(root, state);
    else renderFrameWheel(root, state);
  }

  function showPopup(config, pending) {
    pushEvent("spin_wheel_shown", { page_path: location.pathname, resumed: !!pending });
    var root = buildModalShell();
    root.state = buildState(config, pending, null);
    document.body.appendChild(root.overlay);
    renderInitialFrame(root, root.state);
    openModal(root);
  }

  // ---- inline section -----------------------------------------------------
  // The same wheel + spin + claim experience rendered in-page (a theme
  // section) instead of an overlay. It shares the popup's config, backend,
  // once-per-visitor blob, and every frame renderer; only the shell differs
  // (no overlay, no close, no scroll lock — see the root.inline guards). It
  // renders regardless of config.enabled — that flag gates ONLY the popup — so
  // the section keeps working with the popup switched off. Placement is the
  // theme editor's job: present the section = show it, remove it = hide it.

  function buildInlineShell(mountEl) {
    var content = el("div", { class: "sw-content" });
    var card = el("div", { class: "sw-modal sw-modal--inline", "aria-label": "Spin the wheel" }, [content]);
    var wrap = el("div", { class: "sw-inline" }, [card]);
    while (mountEl.firstChild) mountEl.removeChild(mountEl.firstChild);
    mountEl.appendChild(wrap);
    // inline:true is the single flag every overlay-only path checks — close,
    // scroll-lock, focus-steal and the "no thanks" dismiss all no-op inline.
    return { overlay: null, modal: card, closeBtn: null, content: content, wheel: null, state: null, inline: true, mountEl: mountEl };
  }

  function showInline(config, mountEl) {
    var blob = safeGetBlob();
    var pending =
      blob.pending && blob.pending.spinId && Date.now() - (blob.pending.spunAt || 0) < PENDING_MAX_AGE_MS
        ? blob.pending
        : null;
    var completed = blob.completed
      ? { code: blob.code, prize: blob.prize, conditionsText: blob.conditionsText, productHandle: blob.productHandle }
      : null;
    pushEvent("spin_wheel_inline_shown", {
      page_path: location.pathname,
      state: completed ? "completed" : pending ? "pending" : "fresh",
    });
    var root = buildInlineShell(mountEl);
    root.state = buildState(config, pending, completed);
    renderInitialFrame(root, root.state);
  }

  function clearMounts(mounts) {
    // Drop the Liquid loading skeleton so a section that won't render a wheel
    // (disabled / misconfigured / errored) doesn't spin forever.
    mounts.forEach(function (m) {
      while (m.firstChild) m.removeChild(m.firstChild);
    });
  }

  function initInline() {
    var mounts = document.querySelectorAll("[data-spin-wheel-inline]");
    if (!mounts.length) return;
    loadConfig(safeGetBlob())
      .then(function (config) {
        // Independent of the popup's `enabled` flag, but still bound by the
        // master switch: wheelEnabled:false retires the wheel everywhere. Needs
        // a valid wheel (>=2 wedges) too.
        if (!config || config.wheelEnabled === false || !config.segments || config.segments.length < 2) {
          clearMounts(mounts);
          return;
        }
        ensureStylesheet();
        mounts.forEach(function (mountEl) {
          if (mountEl.__swMounted) return;
          mountEl.__swMounted = true;
          try {
            showInline(config, mountEl);
          } catch (e) {
            // A broken section must never break the page it's embedded on — and
            // must not leave the loading skeleton spinning forever either.
            clearMounts([mountEl]);
          }
        });
      })
      .catch(function () {
        clearMounts(mounts);
      });
  }

  function boot() {
    try {
      init();
    } catch (e) {
      /* the popup must never be able to break the page it's embedded on */
    }
    try {
      initInline();
    } catch (e) {
      /* nor the section */
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
